import {
  ApolloError,
  type ApolloQueryResult,
  NetworkStatus,
  type OperationVariables,
} from '@apollo/client'
import { type LazyResponse } from './useApolloRequestLazyWrapper'

export function useMappedArrayLazyWrapper<
  TResult,
  TPayload,
  TData extends ApolloQueryResult<TPayload>,
  TVariables extends OperationVariables = OperationVariables
>({
  responses,
  variables,
  mapResponse,
  execute,
  isLoading,
}: {
  responses: TData[] | null
  variables: TVariables | null
  execute: (options?: { variables?: TVariables }) => void
  isLoading: boolean
  mapResponse: (data: TData[], variables: TVariables) => TResult
}): LazyResponse<TResult, TVariables> {
  const graphQLErrors = (responses ?? []).flatMap(result => result.errors ?? [])
  const hasErrors = graphQLErrors.length > 0

  if (hasErrors) {
    const error = new ApolloError({
      graphQLErrors,
      errorMessage: `Unable to fetch data`,
    })
    return {
      type: 'error',
      isLoading: false,
      error,
      execute,
      // TODO: validate this
      networkStatus: NetworkStatus.ready,
    }
  }
  if (isLoading) {
    return {
      type: 'pending',
      isLoading: true,
      execute,
      networkStatus: NetworkStatus.loading,
    }
  }
  if (responses === null || variables === null) {
    return {
      type: 'pending',
      isLoading: false,
      execute,
      networkStatus: NetworkStatus.ready,
    }
  }
  const parsedData = mapResponse(responses, variables)
  return {
    type: 'success',
    data: parsedData,
    isLoading: false,
    execute,
    networkStatus: NetworkStatus.ready,
  }
}
